/**
 * Security utilities for frontend protection
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize untrusted email HTML before rendering via dangerouslySetInnerHTML.
 * Strips scripts, event handlers, and other XSS vectors while keeping safe markup.
 */
export function sanitizeEmailHtml(html: string | null | undefined): string {
  return DOMPurify.sanitize(html ?? '', { USE_PROFILES: { html: true } });
}

/**
 * Sanitize HTML to prevent XSS attacks
 */
export function sanitizeHtml(html: string): string {
  const element = document.createElement('div');
  element.textContent = html;
  return element.innerHTML;
}

/**
 * Escape HTML entities
 */
export function escapeHtml(str: string): string {
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  return str.replace(/[&<>"'/]/g, (match) => htmlEscapes[match] ?? match);
}

/**
 * Validate and sanitize URLs
 */
export function sanitizeUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Only allow http, https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Check if URL is from allowed origin
 */
export function isAllowedOrigin(url: string, allowedOrigins: string[]): boolean {
  try {
    const parsed = new URL(url);
    return allowedOrigins.includes(parsed.origin);
  } catch {
    return false;
  }
}

/**
 * Validate a redirect URL for security
 * Ensures URL is safe to redirect to (prevents open redirect attacks)
 *
 * @param url - The URL to validate
 * @param allowedOrigins - Array of allowed origin URLs (defaults to current origin)
 * @returns The validated URL or null if invalid
 * @throws Error if URL is potentially malicious
 */
export function validateRedirectUrl(
  url: string,
  allowedOrigins: string[] = [window.location.origin]
): string {
  // First sanitize the URL
  const sanitized = sanitizeUrl(url);
  if (!sanitized) {
    throw new Error('Invalid redirect URL format');
  }

  // Check if it's from an allowed origin
  if (!isAllowedOrigin(sanitized, allowedOrigins)) {
    throw new Error(
      `Redirect URL must be from one of: ${allowedOrigins.join(', ')}`
    );
  }

  // Additional check: ensure no javascript: or data: URLs slipped through
  const parsed = new URL(sanitized);
  if (!['http:', 'https:'].includes(parsed.protocol)) {
    throw new Error('Redirect URL must use http or https protocol');
  }

  return sanitized;
}

/**
 * Generate CSRF token for forms
 */
export function generateCsrfToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash sensitive data (for client-side comparison only)
 */
export async function hashData(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Validate password strength
 */
export interface PasswordStrength {
  score: number; // 0-4
  feedback: string[];
  isStrong: boolean;
}

export function checkPasswordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Password should be at least 8 characters');
  }

  if (password.length >= 12) {
    score += 1;
  }

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Include both uppercase and lowercase letters');
  }

  if (/\d/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Include at least one number');
  }

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Include at least one special character');
  }

  return {
    score: Math.min(4, Math.floor(score)),
    feedback,
    isStrong: score >= 3,
  };
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Rate limit helper for client-side rate limiting
 */
export class RateLimiter {
  private attempts: number[] = [];
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number, windowMs: number) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  canAttempt(): boolean {
    const now = Date.now();
    this.attempts = this.attempts.filter((time) => now - time < this.windowMs);
    return this.attempts.length < this.maxAttempts;
  }

  recordAttempt(): void {
    this.attempts.push(Date.now());
  }

  getTimeUntilReset(): number {
    if (this.attempts.length === 0) return 0;
    const oldestAttempt = this.attempts[0];
    if (!oldestAttempt) return 0;
    return Math.max(0, this.windowMs - (Date.now() - oldestAttempt));
  }

  reset(): void {
    this.attempts = [];
  }
}

/**
 * Secure storage wrapper with encryption support
 */
export const secureStorage = {
  setItem: (key: string, value: string, encrypt = false): void => {
    const storageValue = encrypt ? btoa(value) : value;
    localStorage.setItem(key, storageValue);
  },

  getItem: (key: string, decrypt = false): string | null => {
    const value = localStorage.getItem(key);
    if (!value) return null;
    return decrypt ? atob(value) : value;
  },

  removeItem: (key: string): void => {
    localStorage.removeItem(key);
  },

  clear: (): void => {
    localStorage.clear();
  },
};

/**
 * Content Security Policy violations logger
 */
export function setupCspViolationLogger(): void {
  document.addEventListener('securitypolicyviolation', (event) => {
    console.error('CSP Violation:', {
      blockedUri: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
    });
  });
}
